import React from 'react';
import { getFieldLabel as _fieldLabel } from '@/lib/fieldLabel';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { getTooltipTheme } from '@/lib/tooltipTheme';

/**
 * PivotTableRenderer — renders a crosstab/pivot table from pivotQuery results.
 * 
 * Props:
 * - pivotResult: { data, grand_total, pivot_columns, row_dimensions, column_dimension, measures, is_pivoted }
 * - vizConfig: { grandTotals, subtotals, compact, columnAlignments, columnWidths, striped }
 * - formatValue: function to format numbers
 * - allFields: array of field metadata
 * - onDrill: callback for cross-filtering
 */
export const PivotTableRenderer = React.memo(function PivotTableRenderer({ pivotResult, vizConfig, formatValue, allFields, onDrill }) {
  if (!pivotResult?.is_pivoted || !pivotResult.data?.length) {
    return <p className="text-xs text-muted-foreground text-center py-8">Set Category (rows) and Color/Series (columns) to create a pivot table</p>;
  }

  const cfg = vizConfig || {};
  const { data, grand_total, pivot_columns, row_dimensions, measures } = pivotResult;

  const getFieldLabel = (name) => {
    const f = (allFields || []).find(ff => ff.name === name);
    return _fieldLabel(f, name);
  };

  const getMeasureLabel = (name) => {
    const f = (allFields || []).find(ff => ff.name === name);
    return _fieldLabel(f, name);
  };

  // Theme-aware hover tooltip for a pivot field header (row dim / measure).
  const tip = getTooltipTheme();
  const FieldTip = ({ name, label }) => {
    const f = (allFields || []).find(ff => ff.name === name);
    if (!f || (!f.description && !f.name)) return <>{label}</>;
    return (
      <Tooltip delayDuration={400}>
        <TooltipTrigger asChild><span className="cursor-help">{label}</span></TooltipTrigger>
        <TooltipContent
          side="top"
          align="start"
          sideOffset={6}
          style={{ backgroundColor: tip.bg, color: tip.fg, borderColor: tip.border }}
          className="max-w-[260px] border shadow-lg normal-case tracking-normal"
        >
          <div className="space-y-0.5">
            {f.description && <p className="text-xs font-normal" style={{ color: tip.fg }}>{f.description}</p>}
            <p className="text-[9px] font-mono" style={{ color: tip.sub }}>{f.name}</p>
          </div>
        </TooltipContent>
      </Tooltip>
    );
  };

  // Build column headers: each pivot_column × each measure
  const colHeaders = [];
  pivot_columns.forEach(cv => {
    measures.forEach(m => {
      colHeaders.push({ colValue: cv, measure: m, key: `${cv}__${m}` });
    });
  });

  const showGrandTotals = cfg.grandTotals !== false;
  const isCompact = cfg.compact || false;
  const isStriped = cfg.striped !== false;

  const cellPad = isCompact ? 'px-2 py-0.5' : 'px-3 py-1.5';
  const fontSize = isCompact ? 'text-[9px]' : 'text-[11px]';
  const headerFontSize = isCompact ? 'text-[8px]' : 'text-[10px]';

  return (
    <TooltipProvider delayDuration={400}>
    <div className="overflow-auto w-full h-full" data-testid="pivot-table">
      <table className={`w-full border-separate ${fontSize}`} style={{ borderSpacing: 0 }}>
        <thead className="sticky top-0 z-10 bg-white dark:bg-[#0b0b0d]">
          {/* Top header row: row dim labels + column dimension values spanning measures */}
          <tr className="bg-muted/40 border-b border-border/50">
            {row_dimensions.map((rd, i) => (
              <th key={rd} rowSpan={measures.length > 1 ? 2 : 1} className={`${cellPad} ${headerFontSize} font-semibold text-muted-foreground text-left uppercase tracking-wider border-r border-border/30 bg-muted/40 sticky left-0`} style={{ minWidth: 100 }}>
                <FieldTip name={rd} label={getFieldLabel(rd)} />
              </th>
            ))}
            {pivot_columns.map(cv => (
              <th key={cv} colSpan={measures.length} className={`${cellPad} ${headerFontSize} font-semibold text-foreground text-center border-r border-border/30 bg-muted/30`}>
                {cv}
              </th>
            ))}
            {showGrandTotals && (
              <th colSpan={measures.length} className={`${cellPad} ${headerFontSize} font-semibold text-foreground text-center bg-primary/10 border-l-2 border-primary/20`}>
                Total
              </th>
            )}
          </tr>
          {/* Sub-header row: measure names (only if multiple measures) */}
          {measures.length > 1 && (
            <tr className="bg-muted/20 border-b border-border/50">
              {pivot_columns.map(cv => (
                measures.map(m => (
                  <th key={`${cv}__${m}`} className={`${cellPad} ${headerFontSize} font-medium text-muted-foreground text-right border-r border-border/20`}>
                    <FieldTip name={m} label={getMeasureLabel(m)} />
                  </th>
                ))
              ))}
              {showGrandTotals && measures.map(m => (
                <th key={`total__${m}`} className={`${cellPad} ${headerFontSize} font-medium text-muted-foreground text-right bg-primary/5 border-l border-primary/10`}>
                  <FieldTip name={m} label={getMeasureLabel(m)} />
                </th>
              ))}
            </tr>
          )}
          {/* Single measure sub-header showing measure name */}
          {measures.length === 1 && (
            <tr className="bg-muted/20 border-b border-border/50">
              {pivot_columns.map(cv => (
                <th key={`${cv}__${measures[0]}`} className={`${cellPad} ${headerFontSize} font-medium text-muted-foreground text-right border-r border-border/20`}>
                  <FieldTip name={measures[0]} label={getMeasureLabel(measures[0])} />
                </th>
              ))}
              {showGrandTotals && (
                <th className={`${cellPad} ${headerFontSize} font-medium text-muted-foreground text-right bg-primary/5 border-l-2 border-primary/20`}>
                  <FieldTip name={measures[0]} label={getMeasureLabel(measures[0])} />
                </th>
              )}
            </tr>
          )}
        </thead>
        <tbody>
          {data.map((row, rowIdx) => {
            // Compute row total
            const rowTotal = {};
            measures.forEach(m => {
              rowTotal[m] = pivot_columns.reduce((sum, cv) => sum + (row[`${cv}__${m}`] || 0), 0);
            });

            return (
              <tr key={rowIdx} className={`border-b border-border/20 hover:bg-muted/20 transition-colors ${isStriped && rowIdx % 2 === 1 ? 'bg-muted/10' : ''}`}
                onClick={() => onDrill && row_dimensions.length > 0 && onDrill(row[row_dimensions[0]])}>
                {row_dimensions.map((rd, i) => (
                  <td key={rd} className={`${cellPad} font-medium text-foreground border-r border-border/30 sticky left-0 bg-background`} style={{ minWidth: 100 }}>
                    {row[rd]}
                  </td>
                ))}
                {colHeaders.map(ch => {
                  const val = row[ch.key];
                  return (
                    <td key={ch.key} className={`${cellPad} text-right text-foreground border-r border-border/10 tabular-nums`}>
                      {val !== undefined && val !== null ? formatValue(val) : '—'}
                    </td>
                  );
                })}
                {showGrandTotals && measures.map(m => (
                  <td key={`total_${m}`} className={`${cellPad} text-right font-semibold text-foreground bg-primary/5 border-l-2 border-primary/20 tabular-nums`}>
                    {formatValue(rowTotal[m])}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
        {/* Grand total footer */}
        {showGrandTotals && grand_total && (
          <tfoot>
            <tr className="border-t-2 border-border/50 bg-muted/30 font-semibold">
              {row_dimensions.map((rd, i) => (
                <td key={rd} className={`${cellPad} text-foreground border-r border-border/30 sticky left-0 bg-muted/30`}>
                  {i === 0 ? 'Grand Total' : ''}
                </td>
              ))}
              {colHeaders.map(ch => (
                <td key={ch.key} className={`${cellPad} text-right text-foreground border-r border-border/10 tabular-nums`}>
                  {grand_total[ch.key] !== undefined ? formatValue(grand_total[ch.key]) : '—'}
                </td>
              ))}
              {measures.map(m => {
                const total = colHeaders.filter(ch => ch.measure === m).reduce((sum, ch) => sum + (grand_total[ch.key] || 0), 0);
                return (
                  <td key={`gt_${m}`} className={`${cellPad} text-right font-bold text-foreground bg-primary/10 border-l-2 border-primary/20 tabular-nums`}>
                    {formatValue(total)}
                  </td>
                );
              })}
            </tr>
          </tfoot>
        )}
      </table>
      <p className="text-[9px] text-muted-foreground/70 mt-1 px-2">{data.length} rows × {pivot_columns.length} columns</p>
    </div>
    </TooltipProvider>
  );
});
